import { useCallback, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import {
  DesktopOutlined,
  ContainerOutlined,
  LikeOutlined,
  IdcardOutlined,
  PlusOutlined,
  TeamOutlined,
  UnorderedListOutlined,
  AudioMutedOutlined,
} from "@ant-design/icons";
import { Grid } from "antd";
import { ItemType } from "antd/lib/menu/interface";

import { featureFlags } from "@settings";

import { Logo } from "@components/Logo";
import { useClientSettings } from "@hooks/useClientSettings";
import usePermissions from "@hooks/usePermissions";

import { CorbanSelector } from "./CorbanSelector";
import { LogoLink, Menu, Footer, Text, Container, Content } from "./styles";

export const SidebarMenu = () => {
  const BENEFIT_CARD_NAME = useClientSettings().benefitCardName;
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { routes, homeRoute } = usePermissions();

  const currentRoute = pathname.substring(1);
  const routeSplit = currentRoute.split("/");
  const currentSelectedSub1 = routeSplit[1];
  const currentSelectedSub2 = `${routeSplit[0]}/${routeSplit[1]}`;

  const hasUsersCrud = featureFlags.enableUsersCrud;
  const hasSearchNMP = featureFlags.enableSearchNMP;

  const [openKeys, setOpenKeys] = useState<string[]>([
    currentSelectedSub1,
    currentSelectedSub2,
  ]);

  const screens = Grid.useBreakpoint();

  const isAllowedItemType = useCallback(
    (itemType: ItemType) =>
      itemType?.key ? !!routes[`/${itemType.key}`] : true,
    [routes],
  );

  const getAllowedItemTypes = useCallback(
    (
      itemTypeList: ((ItemType & { children?: ItemType[] }) | null)[],
    ): ItemType[] => {
      return itemTypeList.reduce((allowedItemTypes: ItemType[], itemType) => {
        if (itemType && isAllowedItemType(itemType)) {
          if (itemType.children) {
            return [
              ...allowedItemTypes,
              {
                ...itemType,
                children: getAllowedItemTypes(itemType.children),
              },
            ];
          }

          return [...allowedItemTypes, itemType];
        }

        return allowedItemTypes;
      }, []);
    },
    [isAllowedItemType],
  );
  const menuItems = useMemo(() => {
    const fullMenu: ItemType[] = [
      { type: "divider" },
      {
        label: "Serviços",
        key: "servicos",
        icon: <ContainerOutlined style={{ color: "#8A92A6" }} />,
        children: [
          {
            label: BENEFIT_CARD_NAME,
            key: "cartao",
            icon: <IdcardOutlined style={{ color: "#8A92A6" }} />,
            children: [
              {
                // TODO: checar o nome desse produto ou do menu
                label: BENEFIT_CARD_NAME,
                key: "cartao/cartao-beneficio",
                icon: <LikeOutlined style={{ color: "#8A92A6" }} />,
              },
              {
                label: "Agregação de Margem",
                key: "cartao/agregacao-margem",
                icon: <LikeOutlined style={{ color: "#8A92A6" }} />,
              },
            ],
          },
          {
            label: "Consulta Consignado",
            key: "consignado/consignado-privado",
            icon: <LikeOutlined style={{ color: "#70242B" }} />,
          },
          {
            label: "Acompanhamento de proposta",
            key: "consignado/acompanhamento-proposta",
            icon: <LikeOutlined style={{ color: "#70242B" }} />,
          },
        ],
      },
      {
        label: "Contratos",
        key: "contratos",
        icon: <DesktopOutlined style={{ color: "#8A92A6" }} />,
      },
    ];

    if (hasSearchNMP) {
      fullMenu.push({
        label: "Não me perturbe",
        key: "nao-me-perturbe",
        icon: <AudioMutedOutlined style={{ color: "#8A92A6" }} />,
      });
    }

    if (hasUsersCrud) {
      fullMenu.push({
        type: "submenu",
        label: "Usuários",
        key: "menu-usuarios",
        icon: <TeamOutlined style={{ color: "#8A92A6" }} />,
        children: [
          {
            label: "Lista de Usuários",
            key: "usuarios",
            icon: <UnorderedListOutlined style={{ color: "#8A92A6" }} />,
          },
          {
            label: "Solicitar Novo Usuário",
            key: "usuarios/solicitacao",
            icon: <PlusOutlined style={{ color: "#8A92A6" }} />,
          },
        ],
      });
    }

    return getAllowedItemTypes(fullMenu);
  }, [getAllowedItemTypes, hasUsersCrud, hasSearchNMP, BENEFIT_CARD_NAME]);

  const handleClick = ({ key }: { key: string }) => {
    navigate(`/${key}`);
    if (currentRoute === key && key.includes("consignado/consignado-privado")) {
      window.location.href = `/${key}/`;
    }
  };

  return (
    <Container>
      <Content>
        <LogoLink to={homeRoute}>
          <Logo icon={screens.xs} />
        </LogoLink>
        <CorbanSelector />
        <Menu
          mode="inline"
          items={menuItems}
          onClick={handleClick}
          selectedKeys={[currentRoute]}
          openKeys={openKeys}
          onOpenChange={setOpenKeys}
        />
      </Content>

      <Footer>
        <Link to="/termos-de-uso">
          <Text>Termos de uso</Text>
        </Link>
        <Link to="/politica-de-privacidade">
          <Text>Política de privacidade</Text>
        </Link>
      </Footer>
    </Container>
  );
};
